import { NTF_OPTIONS, methodOptions } from '../../constants/options';
import { METRICS } from '../SavedReports/helpers';

const ATTRIBUTION_MODEL_MAPPINGS = methodOptions.reduce((acc, model) => ({
  ...acc,
  [model.value]: model.text,
}), {});

const CUSTOMER_TYPE_MAPPINGS = NTF_OPTIONS.reduce((acc, type) => ({
  ...acc,
  [type.value]: type.text,
}), {});

export const STATUS_MAPPINGS = {
  SUCCESS: 'Success',
  ERROR: 'Error',
  IN_PROGRESS: 'In Progress',
  EXPIRED: 'Expired',
  EMPTY: 'Empty Result',
};

/**
 * Transforms exportsDisplay data to match the structure of reportDeliveries data
 * @param {Array} exportsData - The old exports data to transform
 * @returns {Array} - Transformed data that matches reportDeliveries structure
 */

export const transformExportDisplayData = exportsData => {
  if (!Array.isArray(exportsData)) {
    console.warn('exportsData is not an array:', exportsData);
    return [];
  }

  return exportsData.map(item => {
    try {
      // Skip invalid items
      if (!item || typeof item !== 'object') {
        console.warn('Invalid export item:', item);
        return null;
      }

      // Extract email addresses from delivery options with better error handling
      let emailRecipients = [];
      try {
        const deliveryLocation = item?.request_body?.delivery_options?.email_delivery_location;

        if (Array.isArray(deliveryLocation)) {
          // If it's an array of objects, extract the deliveryEmail property
          emailRecipients = deliveryLocation.map(email => email.deliveryEmail).filter(Boolean);
        } else if (typeof deliveryLocation === 'object' && deliveryLocation !== null) {
          // If it's a single objec
          emailRecipients = deliveryLocation.deliveryEmail ? [deliveryLocation.deliveryEmail] : [];
        } else if (typeof deliveryLocation === 'string') {
          // If it's a string, use it directly
          emailRecipients = [deliveryLocation];
        }
      } catch (error) {
        console.error('Error extracting email recipients:', error);
      }

      return {
        id: item.id || Math.random().toString(36).substr(2, 9), // Generate ID if missing
        report_name: item.report_name || 'Unnamed Report',
        pixel_source_name: item.pixel_source_name,
        filter_id: item?.request_body?.report_request?.identifier,
        customer_type: item?.request_body?.report_request?.metrics?.ntf_filter || 'all',
        model_type: item?.request_body?.report_request?.metrics?.attr_method || 'even',
        start_date: item?.request_body?.report_request?.start_date,
        end_date: item?.request_body?.report_request?.end_date,
        delivery_timestamp: item.created_at,
        report_config_id: 0, // Adhoc exports
        email_recipients: emailRecipients,
        download_url: item.status === 'SUCCESS' || item.status === 'EXPIRED' ? item.presigned_url_filtered : null,
        requester: item.requester, // Add requester field for filtering
        // Include original data for reference if needed
        _originalData: item,
        // Add status field that isn't in reportDeliveries but useful for display
        delivery_status: item.status === 'SUCCESS' ? 'success'
          : item.status === 'ERROR' ? 'error'
            : item.status === 'EXPIRED' ? 'expired'
              : item.status === 'IN_PROGRESS' ? 'in_progress'
                : item.status === 'EMPTY' ? 'empty' : 'unknown',
      };
    } catch (error) {
      console.error('Error transforming export item:', error, item);
      return null;
    }
  }).filter(Boolean); // Filter out any null items
};

/**
 * Combines and sorts reportDeliveries and transformed exportsDisplay data
 * @param {Array} reportDeliveries - The report deliveries data
 * @param {Array} exportsDisplay - The exports display data to transform
 * @returns {Array} - Combined and sorted data
 */
export const combineAndSortExportsData = (reportDeliveries, exportsDisplay) => {
  const transformedOldExports = transformExportDisplayData(exportsDisplay);
  const combinedData = [
    ...(Array.isArray(reportDeliveries) ? reportDeliveries : []),
    ...transformedOldExports,
  ];

  // Sort combined data by delivery_timestamp (most recent first)
  return [...combinedData].sort((a, b) => {
    // Safely parse dates, defaulting to old date if missing or invalid
    const dateA = a?.delivery_timestamp ? new Date(a.delivery_timestamp) : new Date(0);
    const dateB = b?.delivery_timestamp ? new Date(b.delivery_timestamp) : new Date(0);

    // Sort in descending order (newest first)
    return dateB - dateA;
  });
};

/**
 * Filters exports based on user email
 * @param {Array} exports - The exports to filter
 * @param {string} userEmail - The user's email to filter by
 * @returns {Array} - Filtered exports
 */
export const filterExportsByUser = (exports, userEmail) => {
  if (!Array.isArray(exports)) return [];

  const normalizedUserEmail = userEmail.toLowerCase();

  return exports.filter(exportItem => {
    // Skip null or undefined items
    if (!exportItem) return false;

    // Check requester field
    const requesterMatch = exportItem.requester
      && typeof exportItem.requester === 'string'
      && exportItem.requester.toLowerCase() === normalizedUserEmail;

    // Check email_recipients array
    const recipientMatch = exportItem.email_recipients
      && Array.isArray(exportItem.email_recipients)
      && exportItem.email_recipients.some(email => typeof email === 'string' && email.toLowerCase() === normalizedUserEmail);

    return requesterMatch || recipientMatch;
  });
};

/**
 * Builds structured fields array for displaying export request details in the details drawer
 * @param {Object} exportRequest - The export request object containing all export configuration
 * @param {Array} segmentsData - Array of segments data used for looking up segment names
 * @returns {Array} - Array of formatted field objects with label and info properties for display
 */
export const buildExportRequestDetailsFields = (exportRequest, segmentsData) => {
  if (!exportRequest.request_body) return [];

  // Find segment name
  const getSegmentName = segmentId => {
    const segment = segmentsData.find(s => s.identifier === segmentId);
    return segment ? segment.action_name : segmentId;
  };

  // Get customer type text
  const getCustomerType = customerType => CUSTOMER_TYPE_MAPPINGS[customerType] || customerType;

  // Get attribution model text
  const getAttributionModel = attributionMethod => ATTRIBUTION_MODEL_MAPPINGS[attributionMethod] || attributionMethod;

  // Format dimensions for display
  const formatDimensions = dimensionsArray => {
    if (!dimensionsArray || !Array.isArray(dimensionsArray)) return 'None';

    const formattedDimensions = dimensionsArray.map(dimension => `${dimension[0].toUpperCase()}${dimension.slice(1)}`);
    return formattedDimensions.join(', ');
  };

  const {
    report_name,
    created_at,
    status,
    request_body: {
      report_request: {
        identifier,
        start_date,
        end_date,
        metadata,
        group_by_date,
        metrics: {
          attr_method,
          ntf_filter,
        },
      },
      delivery_options: {
        email_delivery_location,
      },
    },
  } = exportRequest;

  // Format email recipients
  let emailRecipients = '';
  if (Array.isArray(email_delivery_location)) {
    emailRecipients = email_delivery_location.map(email => email.deliveryEmail).join(', ');
  } else if (typeof email_delivery_location === 'string') {
    emailRecipients = email_delivery_location;
  }

  // Build fields array
  const fields = [
    {
      label: 'Export Name',
      info: report_name,
    },
    {
      label: 'Status',
      info: STATUS_MAPPINGS[status] || status.charAt(0).toUpperCase() + status.substring(1) || 'Unknown',
    },
    {
      label: 'Date Range',
      info: `${start_date} - ${end_date}`,
    },
    {
      label: 'Conversion',
      info: getSegmentName(identifier),
    },
    {
      label: 'Customer Type',
      info: getCustomerType(ntf_filter),
    },
    {
      label: 'Attribution Model',
      info: getAttributionModel(attr_method),
    },
    {
      label: 'Group By Dimensions',
      info: formatDimensions(metadata),
    },
    {
      label: 'Group By Time Period',
      info: `${group_by_date[0].toUpperCase()}${group_by_date.slice(1)}`,
    },
    {
      label: 'Email Recipients',
      info: emailRecipients,
    },
    {
      label: 'Metrics',
      info: METRICS.map(metric => metric).join(', '),
    },
    {
      label: 'Report Type',
      info: 'Adhoc',
    },
    {
      label: 'Request Time', // change to last delivered
      info: created_at,
    },
  ];

  return fields;
};
